/**
 * 用户菜单组件
 * 使用 Unpoly 编译器 API 管理组件生命周期
 */

interface UserMenuElements {
  toggleButton: HTMLButtonElement;
  dropdown: HTMLElement;
  avatar: HTMLElement;
  pulse: HTMLElement;
}

export class UserMenu {
  private isOpen = false;
  private elements: UserMenuElements;

  constructor(private container: HTMLElement) {
    // 获取所有必需的 DOM 元素
    this.elements = {
      toggleButton: container.querySelector("[data-user-menu-toggle]")!,
      dropdown: container.querySelector("[data-user-menu-dropdown]")!,
      avatar: container.querySelector("[data-user-menu-avatar]")!,
      pulse: container.querySelector("[data-user-menu-pulse]")!,
    };

    this.init();
  }

  private init(): void {
    // 绑定事件处理器
    this.elements.toggleButton.addEventListener(
      "click",
      this.handleToggleClick,
    );
    document.addEventListener("click", this.handleOutsideClick);
    document.addEventListener("keydown", this.handleEscapeKey);

    // 监听 Unpoly 导航事件，导航时关闭菜单
    up.on("up:link:follow", this.handleNavigation);
  }

  /**
   * 切换菜单状态
   */
  private toggle = (): void => {
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.open();
    } else {
      this.close();
    }
  };

  /**
   * 打开菜单
   */
  private open(): void {
    const { dropdown, toggleButton, avatar, pulse } = this.elements;

    // 显示下拉菜单
    dropdown.classList.remove("hidden");

    // 使用 requestAnimationFrame 触发过渡动画，避免强制回流 (forced reflow)
    requestAnimationFrame(() => {
      // 应用打开状态的样式
      dropdown.classList.remove("opacity-0", "scale-95", "-translate-y-4");
      dropdown.classList.add("opacity-100", "scale-100", "translate-y-0");

      toggleButton.classList.add("shadow-soft");
      avatar.classList.add("ring-primary/40", "scale-95");
      pulse.classList.remove("opacity-0");
      pulse.classList.add("opacity-100");
    });
  }

  /**
   * 关闭菜单
   */
  private close(): void {
    const { dropdown, toggleButton, avatar, pulse } = this.elements;

    // 应用关闭动画
    dropdown.classList.remove("opacity-100", "scale-100", "translate-y-0");
    dropdown.classList.add("opacity-0", "scale-95", "-translate-y-2");

    toggleButton.classList.remove("shadow-soft");
    avatar.classList.remove("ring-primary/40", "scale-95");
    pulse.classList.remove("opacity-100");
    pulse.classList.add("opacity-0");

    // 等待动画完成后隐藏元素
    setTimeout(() => {
      if (!this.isOpen) {
        dropdown.classList.add("hidden");
        dropdown.classList.remove("-translate-y-2");
        dropdown.classList.add("-translate-y-4");
      }
    }, 200);
  }

  /**
   * 处理切换按钮点击
   */
  private handleToggleClick = (e: Event): void => {
    e.stopPropagation();
    this.toggle();
  };

  /**
   * 处理点击外部区域
   */
  private handleOutsideClick = (e: Event): void => {
    if (this.isOpen && !this.container.contains(e.target as Node)) {
      this.close();
      this.isOpen = false;
    }
  };

  /**
   * 处理 ESC 键
   */
  private handleEscapeKey = (e: KeyboardEvent): void => {
    if (this.isOpen && e.key === "Escape") {
      this.close();
      this.isOpen = false;
    }
  };

  /**
   * 处理 Unpoly 导航事件
   */
  private handleNavigation = (): void => {
    if (this.isOpen) {
      this.close();
      this.isOpen = false;
    }
  };

  /**
   * 清理资源（Unpoly 编译器会在元素被移除时调用）
   */
  destroy(): void {
    this.elements.toggleButton.removeEventListener(
      "click",
      this.handleToggleClick,
    );
    document.removeEventListener("click", this.handleOutsideClick);
    document.removeEventListener("keydown", this.handleEscapeKey);
    up.off("up:link:follow", this.handleNavigation);
  }

  /**
   * 注册编译器
   * @param selector 选择器
   */
  static compiler(selector: string) {
    up.compiler(selector, (element: Element) => {
      const menu = new UserMenu(element as HTMLElement);
      return () => menu.destroy();
    });
  }
}
